/**
 * Seller Posts Page
 * Manage and publish company updates
 */

import { useState, useEffect } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { postsAPI } from '../../services/api';
import PostUpdateModal from '../../components/seller/PostUpdateModal';
import {
  Plus,
  Loader2,
  Eye,
  Edit2,
  Trash2,
  Pin,
  PinOff,
  Calendar,
  BarChart3,
  Milestone,
  Link2,
  DollarSign,
  FlaskConical,
  FileCheck,
  Users,
  MessageSquare,
  MoreVertical,
  Globe,
  Lock,
  FileText,
  TrendingUp,
} from 'lucide-react';

const POST_TYPE_CONFIG = {
  pipeline_milestone: { label: 'Pipeline Milestone', icon: Milestone, color: 'purple' },
  partnership_news: { label: 'Partnership', icon: Link2, color: 'blue' },
  fundraising: { label: 'Fundraising', icon: DollarSign, color: 'green' },
  clinical_data: { label: 'Clinical Data', icon: FlaskConical, color: 'teal' },
  regulatory: { label: 'Regulatory', icon: FileCheck, color: 'orange' },
  team_update: { label: 'Team Update', icon: Users, color: 'indigo' },
  conference: { label: 'Conference', icon: Calendar, color: 'pink' },
  general: { label: 'General', icon: MessageSquare, color: 'gray' },
};

const getTypeConfig = (type) => POST_TYPE_CONFIG[type] || POST_TYPE_CONFIG.general;

const colorClasses = {
  purple: 'bg-purple-100 text-purple-700',
  blue: 'bg-blue-100 text-blue-700',
  green: 'bg-green-100 text-green-700',
  teal: 'bg-teal-100 text-teal-700',
  orange: 'bg-orange-100 text-orange-700',
  indigo: 'bg-indigo-100 text-indigo-700',
  pink: 'bg-pink-100 text-pink-700',
  gray: 'bg-gray-100 text-gray-600',
};

export default function Posts() {
  const { userProfile } = useAuth();
  const [loading, setLoading] = useState(true);
  const [posts, setPosts] = useState([]);
  const [filter, setFilter] = useState('all'); // all, active, draft
  const [showModal, setShowModal] = useState(false);
  const [editingPost, setEditingPost] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [actionMenuId, setActionMenuId] = useState(null);
  const [deleting, setDeleting] = useState(null);

  const sellerId = userProfile?.seller_id;

  useEffect(() => {
    if (sellerId) {
      loadPosts();
      loadAnalytics();
    } else {
      setLoading(false);
    }
  }, [sellerId]);

  const loadPosts = async () => {
    try {
      const response = await postsAPI.getBySellerForPublic(sellerId, { status: 'all' });
      setPosts(response.data?.data?.posts || []);
    } catch (error) {
      console.error('Failed to load posts:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadAnalytics = async () => {
    try {
      const response = await postsAPI.getAnalytics(sellerId);
      setAnalytics(response.data?.data);
    } catch (error) {
      console.debug('Failed to load analytics:', error);
    }
  };

  const handleDelete = async (postId) => {
    if (!confirm('Are you sure you want to delete this post?')) return;

    setDeleting(postId);
    try {
      await postsAPI.delete(postId);
      setPosts(posts.filter(p => p.id !== postId));
    } catch (error) {
      console.error('Failed to delete post:', error);
      alert('Failed to delete post');
    } finally {
      setDeleting(null);
      setActionMenuId(null);
    }
  };

  const handleTogglePin = async (postId) => {
    try {
      const response = await postsAPI.togglePin(postId);
      const isPinned = response.data?.data?.is_pinned;
      setPosts(posts.map(p => ({
        ...p,
        is_pinned: p.id === postId ? isPinned : (isPinned ? false : p.is_pinned),
      })));
    } catch (error) {
      console.error('Failed to toggle pin:', error);
    }
    setActionMenuId(null);
  };

  const handleEdit = (post) => {
    setEditingPost(post);
    setShowModal(true);
    setActionMenuId(null);
  };

  const handleSuccess = () => {
    loadPosts();
    loadAnalytics();
    setEditingPost(null);
  };

  const filteredPosts = posts.filter(p => {
    if (filter === 'all') return true;
    if (filter === 'active') return p.status === 'active';
    if (filter === 'draft') return p.status === 'draft';
    return true;
  });

  // Sort: pinned first, then by date
  const sortedPosts = [...filteredPosts].sort((a, b) => {
    if (a.is_pinned && !b.is_pinned) return -1;
    if (!a.is_pinned && b.is_pinned) return 1;
    return new Date(b.created_at) - new Date(a.created_at);
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
      </div>
    );
  }

  if (!sellerId) {
    return (
      <div className="text-center py-12">
        <FileText className="w-12 h-12 text-gray-300 mx-auto mb-4" />
        <h2 className="text-xl font-semibold text-gray-900 mb-2">
          Complete Your Profile First
        </h2>
        <p className="text-gray-600">
          Set up your company profile to start posting updates.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Company Updates</h1>
          <p className="text-gray-600">Share news with pharma BD teams</p>
        </div>
        <button
          onClick={() => {
            setEditingPost(null);
            setShowModal(true);
          }}
          className="inline-flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 font-medium transition-colors"
        >
          <Plus className="w-5 h-5" />
          New Post
        </button>
      </div>

      {/* Analytics Summary */}
      {analytics && (
        <div className="grid grid-cols-3 gap-4">
          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                <FileText className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">{analytics.overview?.total_posts || 0}</p>
                <p className="text-sm text-gray-600">Total Posts</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                <Eye className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">{analytics.overview?.total_views || 0}</p>
                <p className="text-sm text-gray-600">Total Views</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                <Users className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">{analytics.overview?.unique_viewers || 0}</p>
                <p className="text-sm text-gray-600">Unique Viewers</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Filter Tabs */}
      <div className="flex gap-2 border-b border-gray-200">
        {[
          { value: 'all', label: 'All Posts' },
          { value: 'active', label: 'Published' },
          { value: 'draft', label: 'Drafts' },
        ].map(tab => (
          <button
            key={tab.value}
            onClick={() => setFilter(tab.value)}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              filter === tab.value
                ? 'border-purple-600 text-purple-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            {tab.label}
            {tab.value === 'all' && ` (${posts.length})`}
            {tab.value === 'active' && ` (${posts.filter(p => p.status === 'active').length})`}
            {tab.value === 'draft' && ` (${posts.filter(p => p.status === 'draft').length})`}
          </button>
        ))}
      </div>

      {/* Posts List */}
      {sortedPosts.length > 0 ? (
        <div className="space-y-4">
          {sortedPosts.map(post => {
            const config = getTypeConfig(post.post_type);
            const Icon = config.icon;

            return (
              <div
                key={post.id}
                className={`bg-white rounded-xl border ${
                  post.is_pinned ? 'border-purple-300 ring-1 ring-purple-100' : 'border-gray-200'
                } p-6 hover:shadow-sm transition-shadow`}
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    {/* Header row */}
                    <div className="flex items-center gap-3 mb-2">
                      {post.is_pinned && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-purple-100 text-purple-700 text-xs font-medium rounded-full">
                          <Pin className="w-3 h-3" />
                          Pinned
                        </span>
                      )}
                      <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-full ${colorClasses[config.color]}`}>
                        <Icon className="w-3.5 h-3.5" />
                        {config.label}
                      </span>
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full ${
                        post.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'
                      }`}>
                        {post.status === 'active' ? 'Published' : 'Draft'}
                      </span>
                      <span className="inline-flex items-center gap-1 text-xs text-gray-500">
                        {post.visibility === 'public' ? (
                          <><Globe className="w-3 h-3" /> Public</>
                        ) : (
                          <><Lock className="w-3 h-3" /> Buyers Only</>
                        )}
                      </span>
                    </div>

                    {/* Title */}
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">{post.title}</h3>

                    {/* Content preview */}
                    <p className="text-gray-600 text-sm line-clamp-2 mb-3">{post.content}</p>

                    {/* Stats row */}
                    <div className="flex items-center gap-4 text-sm text-gray-500">
                      <span className="flex items-center gap-1">
                        <Calendar className="w-4 h-4" />
                        {post.published_at
                          ? new Date(post.published_at).toLocaleDateString()
                          : 'Not published'}
                      </span>
                      <span className="flex items-center gap-1">
                        <Eye className="w-4 h-4" />
                        {post.view_count || 0} views
                      </span>
                      <span className="flex items-center gap-1">
                        <Users className="w-4 h-4" />
                        {post.unique_viewer_count || 0} unique
                      </span>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="relative">
                    <button
                      onClick={() => setActionMenuId(actionMenuId === post.id ? null : post.id)}
                      className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                    >
                      <MoreVertical className="w-5 h-5 text-gray-500" />
                    </button>

                    {actionMenuId === post.id && (
                      <>
                        <div className="fixed inset-0" onClick={() => setActionMenuId(null)} />
                        <div className="absolute right-0 top-10 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-10">
                          <button
                            onClick={() => handleEdit(post)}
                            className="w-full flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                          >
                            <Edit2 className="w-4 h-4" />
                            Edit Post
                          </button>
                          <button
                            onClick={() => handleTogglePin(post.id)}
                            className="w-full flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                          >
                            {post.is_pinned ? (
                              <><PinOff className="w-4 h-4" /> Unpin</>
                            ) : (
                              <><Pin className="w-4 h-4" /> Pin to Top</>
                            )}
                          </button>
                          <hr className="my-1 border-gray-200" />
                          <button
                            onClick={() => handleDelete(post.id)}
                            disabled={deleting === post.id}
                            className="w-full flex items-center gap-2 px-4 py-2 text-sm text-red-600 hover:bg-red-50"
                          >
                            {deleting === post.id ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <Trash2 className="w-4 h-4" />
                            )}
                            Delete
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
          <FileText className="w-12 h-12 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">No posts yet</h3>
          <p className="text-gray-600 mb-6">
            Share updates to attract pharma BD teams and showcase your progress.
          </p>
          <button
            onClick={() => setShowModal(true)}
            className="inline-flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 font-medium"
          >
            <Plus className="w-5 h-5" />
            Create Your First Post
          </button>
        </div>
      )}

      {/* Post Modal */}
      <PostUpdateModal
        isOpen={showModal}
        onClose={() => {
          setShowModal(false);
          setEditingPost(null);
        }}
        onSuccess={handleSuccess}
        editPost={editingPost}
      />
    </div>
  );
}
